import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  type RefObject,
} from "react";
import styled from "styled-components";
import {
  Popout,
  type TypePopoutProps,
  type EnumPlacements,
} from "@sproutsocial/seeds-react-popout";
import { mergeRefs } from "@sproutsocial/seeds-react-utilities";

import {
  MenuContentProvider,
  MenuToggleProvider,
  type TypeMenuContextProps,
  type TypeMenuProviderProps,
  useMenu,
} from "../MenuContext";
import { MOTION_DURATION_FAST } from "@sproutsocial/seeds-motion/unitless";

export interface TypeMenuRootOwnProps {
  /** The element that is or contains an element that will toggle the menu when clicked.
   *  May contain labels, buttons, inputs or other content as needed. */
  menuToggleElement: React.ReactElement<any>;
  /** Props to pass through to the underlying Popout.
   *  Any Popout prop is valid except those explicitly omitted. */
  popoutProps?: Partial<
    Omit<TypePopoutProps, "isOpen" | "setIsOpen" | "content" | "children">
  >;
  /** The width of the Popout content.
   *  Defaults to 200px or the width of the toggle element, whichever is greater.*/
  width?: TypePopoutProps["width"];
}
export interface TypeMenuRootProps
  extends Partial<TypeMenuContextProps>,
    Pick<TypeMenuProviderProps, "children">,
    TypeMenuRootOwnProps {}

export const CustomPopoutContent = styled(Popout.Content)`
  padding: 0;
  margin-left: 0;
  margin-right: 0;
  overflow: hidden;
`;

export const menuPlacementOrigin: {
  [key in EnumPlacements]?: { originX: number; originY: number };
} = {
  top: { originX: 0.5, originY: 1 },
  ["top-start"]: { originX: 0, originY: 1 },
  ["top-end"]: { originX: 1, originY: 1 },
  bottom: { originX: 0.5, originY: 0 },
  ["bottom-start"]: { originX: 0, originY: 0 },
  ["bottom-end"]: { originX: 1, originY: 0 },
  left: { originX: 1, originY: 0.5 },
  ["left-start"]: { originX: 1, originY: 0 },
  ["left-end"]: { originX: 1, originY: 1 },
  right: { originX: 0, originY: 0.5 },
  ["right-start"]: { originX: 0, originY: 0 },
  ["right-end"]: { originX: 0, originY: 1 },
};

export const menuAnimationConfig = {
  variants: {
    initial: (
      custom: { placement: EnumPlacements } = { placement: "bottom" }
    ) => ({
      ...menuPlacementOrigin[custom.placement],
      scale: 0,
      opacity: 0,
    }),
    animate: (
      custom: { placement: EnumPlacements } = { placement: "bottom" }
    ) => ({
      ...menuPlacementOrigin[custom.placement],
      opacity: 1,
      scale: 1,

      transition: {
        type: "tween",
        duration: MOTION_DURATION_FAST,
        ease: "circOut",
      },
    }),
    exit: {
      opacity: 0,
      scale: 1,
      transition: {
        type: "tween",
        duration: MOTION_DURATION_FAST,
        ease: "circIn",
      },
    },
  },
  initial: "initial",
  animate: "animate",
  exit: "exit",
};

export interface TypeMenuPopoutProps
  extends Pick<Partial<TypeMenuContextProps>, "openMenu" | "closeMenu">,
    TypePopoutProps {}

export const MenuPopout = ({
  content,
  width = "300px",
  isOpen,
  openMenu,
  closeMenu,
  focusLockProps,
  ...popoutProps
}: TypeMenuPopoutProps) => {
  const setIsOpen = useCallback(
    (newIsOpen: boolean) => {
      newIsOpen ? openMenu?.() : closeMenu?.();
    },
    [openMenu, closeMenu]
  );

  return (
    <Popout
      isOpen={!!isOpen}
      setIsOpen={setIsOpen}
      content={(props) => (
        <CustomPopoutContent width={popoutProps.fullWidth ? "initial" : width}>
          {typeof content === "function" ? content(props) : content}
        </CustomPopoutContent>
      )}
      focusLockProps={{
        // correct jerking motion when scrolling while the Popout is open
        // returnFocus: false,
        crossFrame: false,
        ...focusLockProps,
      }}
      disableWrapperAria
      animationConfig={menuAnimationConfig}
      {...popoutProps}
    />
  );
};

/**
 * @description MenuRoot is a utility component used to handle React Context for our Menus. This component should rarely, if ever, be used directly unless building a custom menu type.
 */

export const MenuRoot = ({
  children,
  menuToggleElement,
  popoutProps: { placement = "bottom", focusLockProps, ...popoutProps } = {},
  width,
  ...contextProps
}: TypeMenuRootProps) => {
  const {
    isOpen,
    openMenu,
    closeMenu,
    inputValue,
    getMenuProps,
    autoCloseOnEmpty = false,
  } = contextProps;

  const toggleElementRef = React.useRef<HTMLElement>(null);
  const [finalWidth, setFinalWidth] = React.useState<typeof width>();

  const min_width = 200;

  useLayoutEffect(() => {
    if (width) {
      setFinalWidth(width);
    } else if (toggleElementRef.current?.offsetWidth) {
      const toggleWidth = toggleElementRef.current.offsetWidth;
      setFinalWidth(Math.max(toggleWidth, min_width));
    } else {
      setFinalWidth(min_width);
    }
  }, [width, toggleElementRef.current?.offsetWidth]);

  const menuContext = useMenu(contextProps);

  // Hack to suppress error: "downshift: You forgot to call the getMenuProps getter function on your component / element."
  getMenuProps?.({}, { suppressRefError: true });

  useEffect(() => {
    if (!inputValue || !autoCloseOnEmpty) return;

    if (!menuContext.hasVisibleItems && isOpen) {
      closeMenu?.();
    }
  }, [menuContext.hasVisibleItems, isOpen, inputValue, autoCloseOnEmpty]);

  return (
    <MenuPopout
      placement={placement}
      isOpen={!!isOpen}
      openMenu={openMenu}
      closeMenu={closeMenu}
      content={
        <MenuContentProvider menuContext={menuContext}>
          {children}
        </MenuContentProvider>
      }
      width={finalWidth}
      {...popoutProps}
    >
      {(toggleProps) => (
        <MenuToggleProvider
          menuContext={{
            ...menuContext,
            ...toggleProps,
            ref: mergeRefs<HTMLElement>(
              toggleProps.ref,
              toggleElementRef
            ) as RefObject<HTMLElement>,
          }}
        >
          {menuToggleElement}
        </MenuToggleProvider>
      )}
    </MenuPopout>
  );
};
